import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PredAccTitle,
  PredicAccBodyCont,
  PredicAccDesc,
  PredictiveTileAccCont,
  PredictiveTileAccHeadCont,
  PredictiveTileHeadSide,
  PredScoreVal,
  LittleWormChartCont,
  ExampleStatementTitle,
  ExampleStatementCont,
  ExampleStatement,
  ScoreTrendCont,
  ChartCont,
} from './ReportPredictiveStatesTileEL.styles';
import { getLocaleFormatDate } from '../../../../helpers/helpers';
import { useFilters } from '../../../../hooks/useFilters';
import { IPredicTopic } from '../../../../services/reports';
import { AccArrowCont } from '../../../CategoriesAndTopics/CategoriesAndTopicsTableTile/CategoriesAndTopicsTileAccordionEl/CategoriesAndTopicsTileAccordionEl.styles'; // eslint-disable-line
import LittleWormChart from '../../../LittleWormChart/LittleWormChart';
import ProgressBar from '../../../ProgressBar/ProgressBar';
import ScoreTrend from '../../../ScoreTrend/ScoreTrend';
import Arrow from '@/assets/icons/chevron-left.svg?react';

interface IReportPredictiveStatesTileEL {
  stateData: IPredicTopic;
  filterKey?: string;
  isTotalsTabActive: boolean;
}
const ReportPredictiveStatesTileEL: FC<IReportPredictiveStatesTileEL> = ({
  stateData,
  filterKey,
  isTotalsTabActive,
}) => {
  const { i18n, t } = useTranslation();
  const { topic, score, scoreRating, teamDistribution, benchmarks } = stateData;
  const thisFilterKey = filterKey || 'positiveFilters';
  const title = topic?.title || '';
  const code = topic?.code || '0';
  const exampleStatement = topic?.exampleStatement || '';
  const predictiveStatesKey = 'predictiveStates';
  const { getNestedFilters, applyNestedFilter } = useFilters();
  const predictiveFilters = getNestedFilters(predictiveStatesKey);
  const thisGroupElementFilter = predictiveFilters[thisFilterKey] || {};
  const thisElementFilter = thisGroupElementFilter[code] || {};
  const active = thisElementFilter?.active || false;

  const { historical, survey } = benchmarks ?? {};

  const manageNestedFilters = () => {
    if (active) {
      return applyNestedFilter({ [code]: null }, thisFilterKey, predictiveStatesKey);
    }
    return applyNestedFilter({ [code]: { active: !active } }, thisFilterKey, predictiveStatesKey);
  };

  return (
    <PredictiveTileAccCont id="PredictiveTileAccCont">
      <PredictiveTileAccHeadCont id="PredictiveTileAccHeadCont">
        <PredictiveTileHeadSide onClick={manageNestedFilters} id="PredictiveTileHeadSide">
          <AccArrowCont data-testid="AccArrowCont" active={active}>
            <Arrow />
          </AccArrowCont>
          <PredAccTitle id="PredAccTitle">{title}</PredAccTitle>
          {isTotalsTabActive && (
            <PredScoreVal id="PredScoreVal" data-testid="predScoreVal">
              {score}
              {historical ? (
                <ScoreTrendCont>
                  <ScoreTrend
                    progress={historical?.change}
                    stateType={historical?.changeDirectionality}
                  />
                </ScoreTrendCont>
              ) : null}
            </PredScoreVal>
          )}
        </PredictiveTileHeadSide>
        <ChartCont id="ChartCont">
          {isTotalsTabActive ? (
            <ProgressBar
              barMode={scoreRating}
              contWidth="100%"
              height="18px"
              historicalScore={historical?.score}
              progress={`${score}%`}
              tooltip={`${getLocaleFormatDate(survey?.endDate ?? '', i18n.language)} ${
                survey?.type
              }`}
            />
          ) : (
            <LittleWormChartCont id="LittleWormChartCont" data-testid="littleWormChartCont">
              {teamDistribution && (
                <LittleWormChart
                  weakValue={teamDistribution.weak}
                  normalValue={teamDistribution.normal}
                  strongValue={teamDistribution.strong}
                  size="tiny"
                />
              )}
            </LittleWormChartCont>
          )}
        </ChartCont>
      </PredictiveTileAccHeadCont>
      {active && (
        <PredicAccBodyCont data-testid="PredicAccBodyCont">
          <PredicAccDesc
            id="PredicAccDesc"
            dangerouslySetInnerHTML={{ __html: topic.description || '' }}
          />
          {exampleStatement && (
            <ExampleStatementCont>
              <ExampleStatementTitle>
                {t('report.predictiveStates.exampleStatement')}:
              </ExampleStatementTitle>{' '}
              <ExampleStatement>{exampleStatement}</ExampleStatement>
            </ExampleStatementCont>
          )}
        </PredicAccBodyCont>
      )}
    </PredictiveTileAccCont>
  );
};
export default ReportPredictiveStatesTileEL;
